import React, { useState, useEffect } from 'react';
import { FileText, Calendar, CircleAlert as AlertCircle, Clock, CircleCheck as CheckCircle, Circle as XCircle, Download, Filter, Search, Eye, User, Loader as Loader2, FileDown } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import jsPDF from 'jspdf';

type TipoSolicitacao = 'ausencia' | 'atestado' | 'ajuste_ponto';

interface Solicitacao {
  id: string;
  usuario_id: string;
  tipo: TipoSolicitacao;
  descricao: string;
  data_inicio: string | null;
  data_fim: string | null;
  arquivo_url: string | null;
  status: 'pendente' | 'aprovado' | 'rejeitado';
  data_solicitacao: string;
  resposta_rh: string | null;
  dados_especificos: any;
  usuarios?: {
    nome: string;
    email: string;
    setor: string;
  };
}

const tipoLabels: Record<TipoSolicitacao, string> = {
  ausencia: 'Ausência',
  atestado: 'Atestado Médico',
  ajuste_ponto: 'Ajuste de Ponto'
};

const tipoIcons: Record<TipoSolicitacao, React.ReactNode> = {
  ausencia: <AlertCircle className="w-4 h-4" />,
  atestado: <FileText className="w-4 h-4" />,
  ajuste_ponto: <Clock className="w-4 h-4" />
};

export const RHAdmin: React.FC = () => {
  const [solicitacoes, setSolicitacoes] = useState<Solicitacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState<string>('todos');
  const [filtroTipo, setFiltroTipo] = useState<string>('todos');
  const [busca, setBusca] = useState('');
  const [solicitacaoSelecionada, setSolicitacaoSelecionada] = useState<Solicitacao | null>(null);
  const [respostaRH, setRespostaRH] = useState('');
  const [processando, setProcessando] = useState(false);

  useEffect(() => {
    loadSolicitacoes();
  }, []);

  const loadSolicitacoes = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('rh_solicitacoes')
        .select(`
          *,
          usuarios (
            nome,
            email,
            setor
          )
        `)
        .order('data_solicitacao', { ascending: false });

      if (error) throw error;
      setSolicitacoes(data || []);
    } catch (error: any) {
      console.error('[RHAdmin] Erro ao carregar solicitações:', error);
      toast.error('Erro ao carregar solicitações');
    } finally {
      setLoading(false);
    }
  };

  const handleAtualizarStatus = async (id: string, novoStatus: 'aprovado' | 'rejeitado') => {
    try {
      setProcessando(true);
      const { error } = await supabase
        .from('rh_solicitacoes')
        .update({
          status: novoStatus,
          resposta_rh: respostaRH || null
        })
        .eq('id', id);

      if (error) throw error;

      toast.success(`Solicitação ${novoStatus === 'aprovado' ? 'aprovada' : 'rejeitada'} com sucesso`);
      setSolicitacaoSelecionada(null);
      setRespostaRH('');
      loadSolicitacoes();
    } catch (error: any) {
      console.error('[RHAdmin] Erro ao atualizar status:', error);
      toast.error('Erro ao atualizar solicitação');
    } finally {
      setProcessando(false);
    }
  };

  const gerarPDF = async (solicitacao: Solicitacao) => {
    try {
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      let yPos = 20;

      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text('RELATÓRIO DE SOLICITAÇÃO RH', margin, yPos);
      yPos += 10;

      pdf.setLineWidth(0.5);
      pdf.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 10;

      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');

      const addSection = (title: string, content: string[][]) => {
        pdf.setFont('helvetica', 'bold');
        pdf.setFillColor(240, 240, 240);
        pdf.rect(margin, yPos, contentWidth, 8, 'F');
        pdf.text(title, margin + 2, yPos + 6);
        yPos += 12;

        pdf.setFont('helvetica', 'normal');
        content.forEach(([label, value]) => {
          if (yPos > 270) {
            pdf.addPage();
            yPos = 20;
          }
          pdf.setFont('helvetica', 'bold');
          pdf.text(label + ':', margin, yPos);
          pdf.setFont('helvetica', 'normal');

          const lines = pdf.splitTextToSize(value, contentWidth - 60);
          lines.forEach((line: string, idx: number) => {
            pdf.text(line, margin + 55, yPos + (idx * 5));
          });
          yPos += Math.max(5, lines.length * 5) + 2;
        });
        yPos += 5;
      };

      const infoGeral = [
        ['Tipo', tipoLabels[solicitacao.tipo]],
        ['Status', solicitacao.status.toUpperCase()],
        ['Data da Solicitação', format(new Date(solicitacao.data_solicitacao), 'dd/MM/yyyy HH:mm', { locale: ptBR })]
      ];
      addSection('INFORMAÇÕES GERAIS', infoGeral);

      const dadosColaborador = [
        ['Nome', solicitacao.usuarios?.nome || 'N/A'],
        ['Email', solicitacao.usuarios?.email || 'N/A'],
        ['Setor', solicitacao.usuarios?.setor || 'N/A']
      ];
      addSection('DADOS DO COLABORADOR', dadosColaborador);

      const detalhesSolicitacao: string[][] = [
        ['Descrição', solicitacao.descricao]
      ];
      if (solicitacao.data_inicio) {
        detalhesSolicitacao.push(['Data Início', format(new Date(solicitacao.data_inicio + 'T00:00:00'), 'dd/MM/yyyy', { locale: ptBR })]);
      }
      if (solicitacao.data_fim) {
        detalhesSolicitacao.push(['Data Fim', format(new Date(solicitacao.data_fim + 'T00:00:00'), 'dd/MM/yyyy', { locale: ptBR })]);
      }
      addSection('DETALHES DA SOLICITAÇÃO', detalhesSolicitacao);

      if (solicitacao.dados_especificos && Object.keys(solicitacao.dados_especificos).length > 0) {
        const dadosEspecificos: string[][] = [];
        Object.entries(solicitacao.dados_especificos).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== '') {
            const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            dadosEspecificos.push([label, String(value)]);
          }
        });
        if (dadosEspecificos.length > 0) {
          addSection('INFORMAÇÕES ADICIONAIS', dadosEspecificos);
        }
      }

      if (solicitacao.resposta_rh) {
        addSection('RESPOSTA DO RH', [['Observações', solicitacao.resposta_rh]]);
      }

      yPos += 10;
      pdf.setLineWidth(0.5);
      pdf.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 7;
      pdf.setFontSize(9);
      pdf.setTextColor(128, 128, 128);
      pdf.text(`Relatório gerado em: ${format(new Date(), 'dd/MM/yyyy HH:mm', { locale: ptBR })}`, margin, yPos);

      pdf.save(`relatorio_${solicitacao.tipo}_${solicitacao.usuarios?.nome.replace(/\s+/g, '_')}_${format(new Date(solicitacao.data_solicitacao), 'ddMMyyyy')}.pdf`);

      toast.success('PDF gerado com sucesso');
    } catch (error) {
      console.error('[RHAdmin] Erro ao gerar PDF:', error);
      toast.error('Erro ao gerar PDF');
    }
  };

  const baixarAtestado = async (filePath: string, nomeColaborador: string) => {
    try {
      console.log('[RHAdmin] Iniciando download:', filePath);

      let bucket: string;
      let path: string;

      if (filePath.startsWith('http')) {
        console.log('[RHAdmin] Detectado URL completa, extraindo bucket e path');
        const urlParts = filePath.split('/object/public/');
        if (urlParts.length !== 2) {
          throw new Error('URL inválida');
        }
        const [bucketName, ...pathParts] = urlParts[1].split('/');
        bucket = bucketName;
        path = pathParts.join('/');
      } else {
        console.log('[RHAdmin] Detectado path relativo');
        const [bucketName, ...pathParts] = filePath.split('/');
        bucket = bucketName;
        path = pathParts.join('/');
      }

      console.log('[RHAdmin] Bucket:', bucket, 'Path:', path);

      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(path, 60);

      if (error) {
        console.error('[RHAdmin] Erro ao criar signed URL:', error);
        throw error;
      }

      console.log('[RHAdmin] Signed URL criada com sucesso');

      const response = await fetch(data.signedUrl);
      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const blob = await response.blob();
      console.log('[RHAdmin] Blob recebido, tamanho:', blob.size);

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);

      const ext = path.split('.').pop();
      link.download = `arquivo_${nomeColaborador.replace(/\s+/g, '_')}_${Date.now()}.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      toast.success('Arquivo baixado com sucesso');
    } catch (error: any) {
      console.error('[RHAdmin] Erro detalhado ao baixar arquivo:', error);
      toast.error(`Erro ao baixar arquivo: ${error.message || 'Desconhecido'}`);
    }
  };

  const solicitacoesFiltradas = solicitacoes.filter(sol => {
    const matchStatus = filtroStatus === 'todos' || sol.status === filtroStatus;
    const matchTipo = filtroTipo === 'todos' || sol.tipo === filtroTipo;
    const matchBusca = !busca ||
      sol.usuarios?.nome.toLowerCase().includes(busca.toLowerCase()) ||
      sol.usuarios?.email.toLowerCase().includes(busca.toLowerCase()) ||
      sol.descricao.toLowerCase().includes(busca.toLowerCase());

    return matchStatus && matchTipo && matchBusca;
  });

  const getStatusBadge = (status: string) => {
    const badges = {
      pendente: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-800 dark:text-yellow-300', icon: <Clock className="w-3 h-3" /> },
      aprovado: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-800 dark:text-green-300', icon: <CheckCircle className="w-3 h-3" /> },
      rejeitado: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-800 dark:text-red-300', icon: <XCircle className="w-3 h-3" /> },
    };

    const badge = badges[status as keyof typeof badges] || badges.pendente;

    return (
      <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.icon}
        <span className="capitalize">{status}</span>
      </span>
    );
  };

  const estatisticas = {
    total: solicitacoes.length,
    pendentes: solicitacoes.filter(s => s.status === 'pendente').length,
    aprovadas: solicitacoes.filter(s => s.status === 'aprovado').length,
    rejeitadas: solicitacoes.filter(s => s.status === 'rejeitado').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary">Total</p>
              <p className="text-2xl font-bold text-on-surface">{estatisticas.total}</p>
            </div>
            <FileText className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary">Pendentes</p>
              <p className="text-2xl font-bold text-yellow-600">{estatisticas.pendentes}</p>
            </div>
            <Clock className="w-8 h-8 text-yellow-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary">Aprovadas</p>
              <p className="text-2xl font-bold text-green-600">{estatisticas.aprovadas}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary">Rejeitadas</p>
              <p className="text-2xl font-bold text-red-600">{estatisticas.rejeitadas}</p>
            </div>
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary w-5 h-5" />
              <input
                type="text"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar por nome, email ou descrição..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-on-surface focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
          </div>

          <div className="flex gap-3">
            <select
              value={filtroStatus}
              onChange={(e) => setFiltroStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-on-surface focus:ring-2 focus:ring-primary/20 focus:border-primary"
            >
              <option value="todos">Todos Status</option>
              <option value="pendente">Pendentes</option>
              <option value="aprovado">Aprovadas</option>
              <option value="rejeitado">Rejeitadas</option>
            </select>

            <select
              value={filtroTipo}
              onChange={(e) => setFiltroTipo(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-on-surface focus:ring-2 focus:ring-primary/20 focus:border-primary"
            >
              <option value="todos">Todos Tipos</option>
              <option value="ausencia">Ausencia</option>
              <option value="atestado">Atestado</option>
              <option value="ajuste_ponto">Ajuste de Ponto</option>
            </select>
          </div>
        </div>

        <div className="space-y-4">
          {solicitacoesFiltradas.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-secondary mx-auto mb-4" />
              <p className="text-secondary">Nenhuma solicitação encontrada</p>
            </div>
          ) : (
            solicitacoesFiltradas.map((solicitacao) => (
              <div
                key={solicitacao.id}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-5 hover:border-primary/40 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-primary/10 rounded-lg text-primary">
                        {tipoIcons[solicitacao.tipo]}
                      </div>
                      <div>
                        <h3 className="font-semibold text-on-surface">{tipoLabels[solicitacao.tipo]}</h3>
                        <p className="text-sm text-secondary">
                          {format(new Date(solicitacao.data_solicitacao), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                        </p>
                      </div>
                      {getStatusBadge(solicitacao.status)}
                    </div>

                    <div className="flex items-center gap-2 mb-2 text-sm text-secondary">
                      <User className="w-4 h-4" />
                      <span className="font-medium text-on-surface">{solicitacao.usuarios?.nome}</span>
                      <span>|</span>
                      <span>{solicitacao.usuarios?.setor}</span>
                      <span>|</span>
                      <span>{solicitacao.usuarios?.email}</span>
                    </div>

                    <p className="text-on-surface mb-3">{solicitacao.descricao}</p>

                    {solicitacao.data_inicio && (
                      <div className="flex items-center gap-2 text-sm text-secondary">
                        <Calendar className="w-4 h-4" />
                        <span>
                          {format(new Date(solicitacao.data_inicio + 'T00:00:00'), 'dd/MM/yyyy', { locale: ptBR })}
                          {solicitacao.data_fim && ` até ${format(new Date(solicitacao.data_fim + 'T00:00:00'), 'dd/MM/yyyy', { locale: ptBR })}`}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    {solicitacao.arquivo_url && (
                      <button
                        onClick={() => baixarAtestado(solicitacao.arquivo_url!, solicitacao.usuarios?.nome || 'colaborador')}
                        className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                        title="Baixar Atestado/Comprovante"
                      >
                        <FileDown className="w-5 h-5" />
                      </button>
                    )}
                    <button
                      onClick={() => gerarPDF(solicitacao)}
                      className="p-2 text-secondary hover:bg-surface-container rounded-lg transition-colors"
                      title="Gerar Relatório PDF"
                    >
                      <Download className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => {
                        setSolicitacaoSelecionada(solicitacao);
                        setRespostaRH(solicitacao.resposta_rh || '');
                      }}
                      className="p-2 text-primary hover:bg-primary/10 rounded-lg transition-colors"
                      title="Ver Detalhes"
                    >
                      <Eye className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {solicitacaoSelecionada && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-on-surface">Detalhes da Solicitação</h3>
                <button
                  onClick={() => setSolicitacaoSelecionada(null)}
                  className="text-secondary hover:text-on-surface"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-on-surface mb-2">
                  Resposta do RH
                </label>
                <textarea
                  value={respostaRH}
                  onChange={(e) => setRespostaRH(e.target.value)}
                  rows={4}
                  placeholder="Escreva uma resposta para o colaborador..."
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-on-surface focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>

              {solicitacaoSelecionada.status === 'pendente' && (
                <div className="flex gap-3">
                  <button
                    onClick={() => handleAtualizarStatus(solicitacaoSelecionada.id, 'aprovado')}
                    disabled={processando}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {processando ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5" />
                        <span>Aprovar</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => handleAtualizarStatus(solicitacaoSelecionada.id, 'rejeitado')}
                    disabled={processando}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    {processando ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <XCircle className="w-5 h-5" />
                        <span>Rejeitar</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
